import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { Socket } from 'ngx-socket-io';
import * as pako from 'pako';
import { JourneyWatchService } from './journey-watch.service';
import { Journey } from '@interfaces/journey';
import { actionModalContinueWaiting } from '@static_data/modals';
import { ModalService } from './modal.service';
import { JourneyService } from './journey.service';
import { ConfirmAlertOptions } from 'digitup-lib/dist/digitup-lib/lib/interfaces/other';
import { DriverLicense } from '@interfaces/user';
import { ModalActionConfig } from '@components/action-modal/action-modal.component';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  currentSocketId = '';
  listnerInited = false;
  continueWaitingChecked = false;
  continueWaitingOpened = false;

  constructor(
    private socket: Socket,
    private auth: AngularFireAuth,
    private journeyWatchService: JourneyWatchService,
    private journeyService: JourneyService,
    private utils: DpUtilsService,
    private translate: TranslateService,
    private modalController: ModalController,
    private modalService: ModalService,
  ) {
    this.initSocket();
  }

  private initSocket() {
    this.auth.onAuthStateChanged(async user => {
      if (user) {
        const result = this.socket.connect();
        if (result.id) {
          this.registerUid(user.uid);
          this.currentSocketId = result.id;
        }

        if (!this.listnerInited) {
          this.listnerInited = true;
          this.socket.fromEvent('connect').subscribe(() => {
            console.log('Connected to socket server! Socket ID:', this.socket.ioSocket.id);
            this.currentSocketId = this.socket.ioSocket.id;
            this.registerUid(user.uid);
          });

          this.listnerJourney();
        }
      } else {
        this.socket.disconnect();
        this.currentSocketId = '';
      }
    });
  }

  private registerUid(uid: string) {
    this.socket.emit('set-uid', { uid });
  }

  private listnerJourney() {
    console.log('INIT listnerJourney');
    this.socket.on('journey', async (journey: ArrayBuffer | Journey) => {
      try {
        journey = this.decompressData(journey as ArrayBuffer);
      } catch (error) { }
      if (journey) {
        switch ((journey as Journey).status) {
          case 'NEW':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'DRIVER_FOUND':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'DRIVER_SEARCHING':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'DRIVER_CONFIRMED':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'DRIVER_ARRIVED':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'TRIP_STARTED':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'PENDING':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'TRIP_COMPLETED':
            this.journeyWatchService.nextJourney(journey as Journey)
            break;
          case 'EXPIRED':
            await this.journeyWatchService.destroyJourney();
            await this.modalService.openActionModal({
              title: this.translate.instant('activity.EXPIRED'),
              subtitle: this.translate.instant('activity.messages.EXPIRED'),
              buttons: [
                {
                  id: 'ok',
                  cssClass: 'general',
                  text: 'action_modal.show_error.okay',
                },
              ]
            })
            // await this.utils.showToast({
            //   header: this.translate.instant('activity.EXPIRED'),
            //   message: this.translate.instant('activity.messages.EXPIRED'),
            //   cssClass: 'info',
            //   duration: 15
            // });
            break;
          case 'RESERVE_WAITING':
            await this.utils.showToast({
              message: this.translate.instant('activity.messages.RESERVE_WAITING')
            });
            break;
          case 'CANCELED_BY_USER':
            await this.journeyWatchService.destroyJourney();
            break;
          case 'CANCELED_BY_DRIVER':
            // await this.utils.showToast({
            //   message: this.translate.instant('activity.messages.CANCELED_BY_DRIVER'),
            //   cssClass: 'warning',
            //   duration: 6000
            // });

            await this.modalService.openActionModal(this.createCancelModalConfig(
              `${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.header_driver')} nº${(journey as Journey).id}`,
              (journey as Journey),
              ((journey as Journey).license as DriverLicense)
            ))
            await this.journeyWatchService.destroyJourney();
            await this.dismiss();
            break;
          case 'CANCELED_BY_MANAGER':
            // await this.utils.showToast({
            //   message: this.translate.instant('activity.messages.CANCELED_BY_DRIVER'),
            //   cssClass: 'warning',
            //   duration: 6000
            // });

            // const currentJourney: Journey = journey as Journey; 
            // const driverLicence: DriverLicense = currentJourney.license as DriverLicense;

            await this.modalService.openActionModal(this.createCancelModalConfig(
              `${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.header_manager')} №${(journey as Journey).id}`,
              (journey as Journey),
              ((journey as Journey).license as DriverLicense)
            ))
            await this.journeyWatchService.destroyJourney();
            await this.dismiss();
            break;
          case 'ERROR':
            // await this.utils.showToast({
            //   message: this.translate.instant('activity.messages.ERROR'),
            //   cssClass: 'warning',
            //   duration: 6000
            // });
            await this.modalService.openActionModal({
              title: this.translate.instant('activity.messages.ERROR'),
              buttons: [
                {
                  id: 'ok',
                  cssClass: 'general',
                  text: 'action_modal.show_error.okay',
                },
              ]
            })
            await this.journeyWatchService.destroyJourney();
            await this.dismiss();
            break;
          default:
            // RATING_AND_PAYMENT, CLOSED, CANCELED_BY_USER, CANCELED_BY_DRIVER, CANCEL Without action
            // this.journeyWatchService.nextJourney(journey)
            break;
        }
        const checkJourney = journey as Journey;
        const now = Date.now();
        const journeyCreatedAt = new Date(checkJourney.createdAt).getTime();
        const difference = Math.round((now - journeyCreatedAt) / 1000 / 60);

        if (!checkJourney.priority &&
          ['DRIVER_SEARCHING', 'NEW', 'DRIVER_FOUND'].includes(checkJourney.status) &&
          !checkJourney.user.role.includes('client-hotel') &&
          difference > 30 && !this.continueWaitingChecked && !this.continueWaitingOpened
        ) {
          this.continueWaitingChecked = true;
          this.continueWaitingOpened = true;
          const result = await this.modalService.openActionModal(actionModalContinueWaiting, true);
          if (result?.button?.id === 'yes') {
            console.log('send update to priority');
            await this.journeyService.setPriority(checkJourney.id);
          }
          this.continueWaitingOpened = false;

          setTimeout(() => {
            this.continueWaitingChecked = false;
          }, 60 * 1000);
        }

      }

    });
  }

  private async dismiss() {
    if (await this.modalController.getTop()) {
      await this.modalController.dismiss();
    }
    if (await this.modalController.getTop()) {
      await this.modalController.dismiss();
    }
  }

  /**
 * Decompresses data received via socket. If the data is in ArrayBuffer format,
 * it decompresses and parses it into a GeneralSocket object.
 * If decompression fails, it returns the original data cast as GeneralSocket.
 *
 * @param {ArrayBuffer | GeneralSocket} socketInfo - The data to decompress, which can be an ArrayBuffer or already a GeneralSocket object.
 * @returns {GeneralSocket} - The decompressed and parsed data as a GeneralSocket object, or the original data if an error occurs.
 */
  private decompressData(socketInfo: ArrayBuffer): Journey {
    // Check if the input is an instance of ArrayBuffer before attempting to decompress
    if (socketInfo instanceof ArrayBuffer) {
      try {
        const decompressed = pako.inflate(new Uint8Array(socketInfo), { to: 'string' });
        const decompressParsed = JSON.parse(decompressed);
        return decompressParsed;
      } catch (error) {
        console.error("Failed to decompress or parse the data:", error);
        // Optionally, throw the error to be handled by the caller
        console.error('Decompression or parsing failed.');
        throw new Error('Decompression or parsing failed.');
      }
    } else {
      // console.error('is GeneralSocket');
      throw new Error('is GeneralSocket');
    }

    // If not ArrayBuffer or if there was an error, return the input as GeneralSocket
    // return socketInfo as GeneralSocket;
  }

  createCancelModalConfig(title: string, journey: Journey, driverLicence: DriverLicense): ModalActionConfig {
    return {
      title: title,
      subtitle: `
        <p>${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.message')}</p>
        
        <p><strong>${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.license')}</strong><br>
        ${driverLicence.brand} / ${driverLicence.model} : ${driverLicence.taxiNumber}.</p>
  
        <p><strong>${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.direction')}</strong><br>
        ${journey.fromAddress.title} - ${journey.toAddress.title}.</p>
  
        <p><strong>${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.reason')}</strong><br>
        ${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.canceledReason.' + journey.canceledReason)}.</p>
  
        <p><strong>${this.translate.instant('activity.messages.CANCELED_BY_DRIVER.reserved')}</strong><br>
        ${journey.reserve
          ? this.translate.instant('activity.messages.CANCELED_BY_DRIVER.yes')
          : this.translate.instant('activity.messages.CANCELED_BY_DRIVER.no')}
        </p>
      `,
      buttons: [
        {
          id: 'ok',
          cssClass: 'general',
          text: 'action_modal.show_error.okay',
        },
      ]
    };
  }
}

